import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import * as RadioPrimitive from '@radix-ui/react-radio-group'
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { inputClass } from './input'

/* ── Select ─────────────────────────────────────────────────────────────── */
export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  disabled,
  className,
  'aria-label': ariaLabel,
}: {
  value: string | undefined
  onValueChange: (v: string) => void
  options: Array<{ value: string; label: React.ReactNode; disabled?: boolean }>
  placeholder?: string
  id?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger id={id} aria-label={ariaLabel} className={cn(inputClass, 'items-center justify-between gap-2 text-left', className)}>
        <SelectPrimitive.Value placeholder={placeholder ?? 'Choose…'} />
        <SelectPrimitive.Icon>
          <ChevronDown className="size-3.5 text-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content position="popper" sideOffset={4} className="z-[60] max-h-[320px] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-line bg-surface p-1 shadow-xl">
          <SelectPrimitive.Viewport>
            {options.map(o => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex cursor-pointer select-none items-center rounded-md py-1.5 pl-7 pr-2 text-[12.5px] text-ink outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-soft"
              >
                <SelectPrimitive.ItemIndicator className="absolute left-2">
                  <Check className="size-3.5 text-accent" />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

/* ── Checkbox / Switch / Radio ──────────────────────────────────────────── */
export function Checkbox({ checked, onCheckedChange, id, label, description, disabled }: { checked: boolean; onCheckedChange: (v: boolean) => void; id: string; label: React.ReactNode; description?: React.ReactNode; disabled?: boolean }) {
  return (
    // `relative` contains the hidden form input Radix renders beside the
    // control (position: absolute); without it the input escapes the scroll
    // area and makes the whole page taller than the window.
    <div className="relative flex items-start gap-2.5">
      <CheckboxPrimitive.Root
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={v => onCheckedChange(v === true)}
        className="mt-0.5 grid size-4 shrink-0 place-items-center rounded border border-line bg-surface data-[state=checked]:border-accent data-[state=checked]:bg-accent disabled:opacity-50"
      >
        <CheckboxPrimitive.Indicator>
          <Check className="size-3 text-accent-ink" strokeWidth={3} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <label htmlFor={id} className="grid cursor-pointer gap-0.5 text-[12.5px] leading-snug">
        <span className="text-ink">{label}</span>
        {description && <span className="text-[11.5px] text-muted">{description}</span>}
      </label>
    </div>
  )
}

export function Switch({ checked, onCheckedChange, id, label, description, disabled }: { checked: boolean; onCheckedChange: (v: boolean) => void; id: string; label: React.ReactNode; description?: React.ReactNode; disabled?: boolean }) {
  return (
    // `relative`: see Checkbox — contains Radix's hidden absolute input.
    <div className="relative flex items-start justify-between gap-4">
      <label htmlFor={id} className="grid cursor-pointer gap-0.5 text-[12.5px] leading-snug">
        <span className="font-medium text-ink">{label}</span>
        {description && <span className="text-[11.5px] text-muted">{description}</span>}
      </label>
      <SwitchPrimitive.Root
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="relative h-5 w-9 shrink-0 rounded-full bg-line transition-colors data-[state=checked]:bg-accent disabled:opacity-50"
      >
        <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[18px]" />
      </SwitchPrimitive.Root>
    </div>
  )
}

export function RadioCards<T extends string>({ value, onValueChange, options, name, className }: { value: T; onValueChange: (v: T) => void; options: Array<{ value: T; label: React.ReactNode; description?: React.ReactNode; disabled?: boolean }>; name: string; className?: string }) {
  return (
    <RadioPrimitive.Root value={value} onValueChange={v => onValueChange(v as T)} aria-label={name} className={cn('relative grid gap-2', className)}>
      {options.map(o => (
        <RadioPrimitive.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className="flex w-full items-start gap-3 rounded-xl border border-line bg-surface px-3.5 py-3 text-left transition-colors hover:border-accent data-[state=checked]:border-accent data-[state=checked]:bg-accent-soft disabled:opacity-50"
        >
          <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border border-line bg-surface">
            <RadioPrimitive.Indicator className="size-2 rounded-full bg-accent" />
          </span>
          <span className="grid gap-0.5">
            <span className="text-[12.5px] font-semibold text-ink">{o.label}</span>
            {o.description && <span className="text-[11.5px] leading-snug text-muted">{o.description}</span>}
          </span>
        </RadioPrimitive.Item>
      ))}
    </RadioPrimitive.Root>
  )
}

/* ── Tabs ───────────────────────────────────────────────────────────────── */
export const Tabs = TabsPrimitive.Root
export function TabsList({ tabs, className }: { tabs: Array<{ value: string; label: React.ReactNode; count?: number }>; className?: string }) {
  return (
    <TabsPrimitive.List className={cn('flex gap-1 overflow-x-auto border-b border-line', className)}>
      {tabs.map(t => (
        <TabsPrimitive.Trigger
          key={t.value}
          value={t.value}
          className="-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-[12px] font-semibold text-muted hover:text-ink data-[state=active]:border-accent data-[state=active]:text-ink"
        >
          {t.label}
          {t.count != null && <span className="rounded bg-soft px-1.5 text-[10px] text-muted">{t.count}</span>}
        </TabsPrimitive.Trigger>
      ))}
    </TabsPrimitive.List>
  )
}
export const TabsContent = ({ className, ...p }: React.ComponentProps<typeof TabsPrimitive.Content>) => <TabsPrimitive.Content className={cn('pt-4 outline-none', className)} {...p} />

/* ── Tooltip ────────────────────────────────────────────────────────────── */
export function Tooltip({ content, children, side = 'top' }: { content: React.ReactNode; children: React.ReactElement; side?: 'top' | 'right' | 'bottom' | 'left' }) {
  return (
    <TooltipPrimitive.Root delayDuration={250}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="z-[70] max-w-[260px] rounded-md bg-[#0b1425] px-2 py-1 text-[11px] leading-snug text-white shadow-lg">
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
export const TooltipProvider = TooltipPrimitive.Provider

/* ── Dropdown menu ──────────────────────────────────────────────────────── */
export const Menu = DropdownPrimitive.Root
export const MenuTrigger = DropdownPrimitive.Trigger
export function MenuContent({ children, align = 'end', className }: { children: React.ReactNode; align?: 'start' | 'end' | 'center'; className?: string }) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content align={align} sideOffset={4} className={cn('z-[60] min-w-[180px] rounded-lg border border-line bg-surface p-1 shadow-xl', className)}>
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  )
}
export function MenuItem({ children, onSelect, danger, disabled }: { children: React.ReactNode; onSelect?: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <DropdownPrimitive.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn('flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-soft [&_svg]:size-3.5 [&_svg]:text-muted', danger ? 'text-red [&_svg]:text-red' : 'text-ink')}
    >
      {children}
    </DropdownPrimitive.Item>
  )
}
export const MenuSeparator = () => <DropdownPrimitive.Separator className="my-1 h-px bg-line" />
export const MenuLabel = ({ children }: { children: React.ReactNode }) => <DropdownPrimitive.Label className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted">{children}</DropdownPrimitive.Label>

/* ── Misc ───────────────────────────────────────────────────────────────── */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-soft', className)} aria-hidden />
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="rounded border border-line bg-soft px-1.5 py-0.5 text-[10px] font-medium text-muted">{children}</kbd>
}

/** Segmented control for 2–5 options (filters, ranges). */
export function Segmented<T extends string>({ value, onChange, options, 'aria-label': label }: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: React.ReactNode }>; 'aria-label': string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-lg border border-line bg-surface p-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn('rounded-md px-2.5 py-1 text-[11.5px] font-semibold transition-colors', value === o.value ? 'bg-accent-soft text-accent' : 'text-muted hover:text-ink')}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
